import { Check, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  WorkflowStepInstanceCommentTypes,
  type WorkflowStepInstanceComment,
} from "~/api/types";
import { useUserContext } from "~/hooks/useUserContext";
import { getCommentLabel } from "~/helpers/workflow-instances";
import { cn } from "~/lib/utils";
import { RichTextView } from "~/molecules/RichTextView";
import AvatarWithFallback from "./AvatarWithFallback";

export const StepComments = ({
  comments,
}: {
  comments: WorkflowStepInstanceComment[];
}) => {
  const { t } = useTranslation();
  const { user } = useUserContext();

  if (comments.length === 0) return null;

  return (
    <div className="space-y-3 border-t pt-3">
      <div className="space-y-3">
        {comments.map((comment) => {
          const isCurrentUser = comment.createdBy.userId === user.id;

          const avatar = (
            <AvatarWithFallback
              user={comment.createdBy}
              size="sm"
              showTooltip
            />
          );

          return (
            <div
              key={comment.id}
              className="grid grid-cols-[auto_1fr_auto] items-start gap-1"
            >
              <div>{!isCurrentUser && avatar}</div>

              <div
                className={cn(
                  "bg-card max-w-full min-w-0 rounded-lg",
                  isCurrentUser
                    ? "justify-self-end rounded-tr-none"
                    : "justify-self-start rounded-tl-none",
                )}
              >
                <div
                  className={cn("rounded-[inherit] border p-3", {
                    "border-destructive/30 bg-destructive/2":
                      comment.type ===
                      WorkflowStepInstanceCommentTypes.rejected,
                    "border-success/30 bg-success/2":
                      comment.type ===
                      WorkflowStepInstanceCommentTypes.approved,
                  })}
                >
                  <div className="text-muted-foreground flex items-center gap-1 text-xs">
                    {comment.type ===
                      WorkflowStepInstanceCommentTypes.rejected && (
                      <X className="text-destructive-foreground size-3" />
                    )}
                    {comment.type ===
                      WorkflowStepInstanceCommentTypes.approved && (
                      <Check className="text-success-foreground size-3" />
                    )}
                    <span>{getCommentLabel(comment, t)}</span>
                  </div>
                  {comment.text ? (
                    <RichTextView
                      value={comment.text}
                      className="text-sm wrap-break-word"
                    />
                  ) : null}
                </div>
              </div>

              <div>{isCurrentUser && avatar}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
